import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { FolderIcon, ChevronDownIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { selectActiveFirm } from "@/store/appSelectors";
import { getContentFolder } from "@/sideEffects/api";
import { ContentFolderBaseView } from "./ContentFolderBaseView";

interface Props {
  selectedFolderGuid?: string;
  onSelectFolder: (folderGuid?: string) => void;
}

export const ContentFolderSelectSheet: React.FC<Props> = ({ selectedFolderGuid, onSelectFolder }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const activeFirm = useSelector(selectActiveFirm);

  const { data: folders } = useQuery({
    queryKey: ["contentFolder"],
    queryFn: () => getContentFolder(String(activeFirm?.firmGuid)),
  });

  const selectedFolder = folders?.data?.result.find((folder) => folder.id === selectedFolderGuid);

  const handleSelectFolder = (folderGuid?: string) => {
    onSelectFolder(folderGuid);
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setIsOpen(true)} className="justify-between w-full">
        <div className="flex items-center gap-2">
          <FolderIcon className="h-4 w-4" />
          <span className="truncate">{selectedFolder ? selectedFolder.name : t("folder.selectFolder")}</span>
        </div>
        <ChevronDownIcon className="h-4 w-4" />
      </Button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent className="overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("entities.folder")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <ContentFolderBaseView onSelectFolder={handleSelectFolder} selectedFolderGuid={selectedFolderGuid} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
